import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";

const ALL_TYPES = [
  { id: "system_offline", label: "System Offline", icon: "🔴", color: "#dc2626" },
  { id: "fip_fault",      label: "FIP Fault",      icon: "⚡", color: "#ea580c" },
  { id: "hazard",         label: "Hazard",          icon: "⚠️", color: colors.red },
  { id: "change",         label: "Change",          icon: "🔄", color: colors.amber },
  { id: "service_request",label: "Service Request", icon: "🔧", color: colors.slate },
  { id: "service_note",   label: "Service Note",    icon: "📝", color: colors.teal },
  { id: "parking",        label: "Parking Note",    icon: "🅿️", color: colors.blue },
];

const CRITICAL_TYPES = new Set(["system_offline", "fip_fault", "hazard"]);

function typeInfo(id) {
  return ALL_TYPES.find(t => t.id === id) || { label: id, icon: "📋", color: colors.slate };
}

function fmtDate(ts) {
  return new Date(ts).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Shared form used for both new report creation from management side
function ReportForm({ onClose, onSuccess }) {
  const fileInputRef = useRef(null);
  const [selectedType, setSelectedType] = useState(null);
  const [priority, setPriority] = useState("urgent");
  const [siteSearch, setSiteSearch] = useState("");
  const [siteResults, setSiteResults] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (siteSearch.length < 2) { setSiteResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/sites/`);
        const q = siteSearch.toLowerCase();
        setSiteResults(res.data.filter(s =>
          s.site_name.toLowerCase().includes(q) || s.site_address.toLowerCase().includes(q)
        ).slice(0, 8));
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [siteSearch]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const removePhoto = () => {
    setPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!selectedType) { setError("Select a report type."); return; }
    if (!selectedSite) { setError("Select a site."); return; }
    if (!description.trim()) { setError("Enter a description."); return; }
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("report_type", selectedType);
      fd.append("site", selectedSite.id);
      if (title.trim()) fd.append("title", title.trim());
      fd.append("description", description.trim());
      if (selectedType === "service_request") fd.append("priority", priority);
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/field-reports/`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSuccess();
    } catch {
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: "6px", border: `1px solid ${colors.border}`, fontSize: "14px", outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: "11px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "5px", display: "block" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.3)" }} />
      <div style={{
        position: "relative", zIndex: 1, backgroundColor: colors.white,
        width: "460px", maxWidth: "100vw", height: "100vh",
        overflowY: "auto", boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
        padding: "24px",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
          <h2 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: colors.darkGrey }}>New Report</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: colors.slate }}>✕</button>
        </div>

        {/* Type */}
        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>Report Type</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {ALL_TYPES.map(t => (
              <div key={t.id} onClick={() => setSelectedType(t.id)} style={{
                display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px",
                border: `2px solid ${selectedType === t.id ? t.color : colors.border}`,
                borderRadius: "8px", cursor: "pointer",
                backgroundColor: selectedType === t.id ? `${t.color}10` : colors.white,
              }}>
                <span style={{ fontSize: "18px" }}>{t.icon}</span>
                <span style={{ fontSize: "13px", fontWeight: "600", color: t.color }}>{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Priority for SRs */}
        {selectedType === "service_request" && (
          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>Priority</label>
            <div style={{ display: "flex", gap: "6px" }}>
              {[{ id: "critical", label: "Critical", bg: "#fee2e2", c: "#b91c1c" }, { id: "urgent", label: "Urgent", bg: "#fff7ed", c: "#c2410c" }, { id: "please_do", label: "Please Do", bg: "#dbeafe", c: "#1d4ed8" }].map(p => (
                <button key={p.id} onClick={() => setPriority(p.id)} style={{
                  flex: 1, padding: "8px 0", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer",
                  border: `2px solid ${priority === p.id ? p.c : colors.border}`,
                  backgroundColor: priority === p.id ? p.bg : colors.white,
                  color: priority === p.id ? p.c : colors.slate,
                }}>{p.label}</button>
              ))}
            </div>
          </div>
        )}

        {/* Site */}
        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>Site</label>
          {selectedSite ? (
            <div style={{ padding: "10px 12px", border: `2px solid ${colors.teal}`, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: "600", fontSize: "13px" }}>{selectedSite.site_name}</div>
                <div style={{ fontSize: "11px", color: colors.slate }}>{selectedSite.site_address}</div>
              </div>
              <button onClick={() => { setSelectedSite(null); setSiteSearch(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate }}>✕</button>
            </div>
          ) : (
            <div>
              <input style={inputStyle} placeholder="Search site..." value={siteSearch} onChange={e => setSiteSearch(e.target.value)} />
              {siteResults.length > 0 && (
                <div style={{ border: `1px solid ${colors.border}`, borderRadius: "6px", marginTop: "4px", backgroundColor: colors.white, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                  {siteResults.map(s => (
                    <div key={s.id} onClick={() => { setSelectedSite(s); setSiteSearch(""); setSiteResults([]); }} style={{ padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${colors.border}`, fontSize: "13px" }}>
                      <div style={{ fontWeight: "600" }}>{s.site_name}</div>
                      <div style={{ fontSize: "11px", color: colors.slate }}>{s.site_address}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Title */}
        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>Title (optional)</label>
          <input style={inputStyle} placeholder="Brief title..." value={title} onChange={e => setTitle(e.target.value)} />
        </div>

        {/* Description */}
        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>Description</label>
          <textarea style={{ ...inputStyle, minHeight: "100px", resize: "vertical" }} placeholder="Describe the issue..." value={description} onChange={e => setDescription(e.target.value)} />
        </div>

        {/* Photo */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>Photo (optional)</label>
          {photoPreview ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img src={photoPreview} alt="Preview" style={{ width: "72px", height: "72px", objectFit: "cover", borderRadius: "6px", border: `1px solid ${colors.border}` }} />
              <button onClick={removePhoto} style={{ fontSize: "12px", color: colors.red, background: "none", border: "none", cursor: "pointer", fontWeight: "600" }}>Remove</button>
            </div>
          ) : (
            <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 14px", border: `2px dashed ${colors.border}`, borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "600", color: colors.slate, backgroundColor: "#f8fafc" }}>
              📷 Attach photo
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: "none" }} />
            </label>
          )}
        </div>

        {error && <div style={{ backgroundColor: "#fdecea", border: `1px solid ${colors.red}`, borderRadius: "6px", padding: "10px 12px", color: colors.red, fontSize: "13px", marginBottom: "14px" }}>{error}</div>}

        <button onClick={handleSubmit} disabled={submitting} style={{
          width: "100%", backgroundColor: submitting ? colors.slate : colors.darkGrey,
          color: "white", border: "none", padding: "13px", borderRadius: "8px",
          fontSize: "14px", fontWeight: "700", cursor: submitting ? "not-allowed" : "pointer",
        }}>
          {submitting ? "Submitting..." : "Submit Report"}
        </button>
      </div>
    </div>
  );
}

function EditReportPanel({ report, onClose, onSaved, onResolved }) {
  const fileInputRef = useRef(null);
  const t = typeInfo(report.report_type);

  const [title, setTitle] = useState(report.title || "");
  const [description, setDescription] = useState(report.description || "");
  const [newPhoto, setNewPhoto] = useState(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState("");

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setNewPhoto(file);
    setNewPhotoPreview(URL.createObjectURL(file));
  };

  const removeNewPhoto = () => {
    setNewPhoto(null);
    if (newPhotoPreview) URL.revokeObjectURL(newPhotoPreview);
    setNewPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = async () => {
    if (!description.trim()) { setError("Description is required."); return; }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      if (newPhoto) fd.append("photo", newPhoto);
      const res = await axios.patch(`${API}/field-reports/${report.id}/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSaved(res.data);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleResolve = async () => {
    setResolving(true);
    try {
      await axios.patch(`${API}/field-reports/${report.id}/`, { is_resolved: true });
      onResolved(report.id);
    } catch {
      setError("Failed to resolve. Please try again.");
    } finally {
      setResolving(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: "6px",
    border: `1px solid ${colors.border}`, fontSize: "14px", outline: "none", boxSizing: "border-box",
  };
  const labelStyle = {
    fontSize: "11px", fontWeight: "700", color: colors.slate,
    textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "5px", display: "block",
  };

  // Current photo to display: new preview takes priority, else existing
  const displayPhoto = newPhotoPreview || report.photo_url;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.3)" }} />
      <div style={{
        position: "relative", zIndex: 1, backgroundColor: colors.white,
        width: "480px", maxWidth: "100vw", height: "100vh",
        overflowY: "auto", boxShadow: "-4px 0 24px rgba(0,0,0,0.15)", padding: "24px",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "20px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
              <span style={{ fontSize: "20px" }}>{t.icon}</span>
              <span style={{ fontWeight: "700", fontSize: "15px", color: t.color }}>{t.label}</span>
              <span style={{
                fontSize: "10px", fontWeight: "700", padding: "2px 8px", borderRadius: "12px",
                backgroundColor: report.is_resolved ? "#e8f8f1" : "#fef9e7",
                color: report.is_resolved ? colors.green : colors.amber,
              }}>
                {report.is_resolved ? "Resolved" : "Open"}
              </span>
            </div>
            <div style={{ fontSize: "13px", fontWeight: "600", color: colors.darkGrey }}>{report.site_name}</div>
            <div style={{ fontSize: "11px", color: colors.slate }}>{report.site_address}</div>
            <div style={{ fontSize: "11px", color: colors.slate, marginTop: "2px" }}>
              {report.submitted_by_name} · {fmtDate(report.submitted_at)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: colors.slate, flexShrink: 0, padding: "0 0 0 12px" }}>✕</button>
        </div>

        {/* Photo */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>Photo</label>
          {displayPhoto ? (
            <div style={{ position: "relative", display: "inline-block" }}>
              <a href={newPhotoPreview || report.photo_url} target="_blank" rel="noopener noreferrer">
                <img
                  src={displayPhoto}
                  alt="Report"
                  style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "8px", border: `1px solid ${colors.border}`, display: "block" }}
                />
              </a>
              <label style={{
                position: "absolute", bottom: "6px", right: "6px",
                backgroundColor: "rgba(0,0,0,0.6)", color: "white",
                fontSize: "10px", fontWeight: "600", padding: "3px 8px", borderRadius: "4px",
                cursor: "pointer",
              }}>
                {newPhotoPreview ? "✓ New" : "Replace"}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: "none" }} />
              </label>
              {newPhotoPreview && (
                <button onClick={removeNewPhoto} style={{
                  position: "absolute", top: "4px", right: "4px",
                  background: "rgba(0,0,0,0.5)", border: "none", color: "white",
                  width: "20px", height: "20px", borderRadius: "50%", cursor: "pointer", fontSize: "11px", lineHeight: "1",
                }}>✕</button>
              )}
            </div>
          ) : (
            <label style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "10px 16px", border: `2px dashed ${colors.border}`, borderRadius: "8px",
              cursor: "pointer", fontSize: "13px", fontWeight: "600", color: colors.slate, backgroundColor: "#f8fafc",
            }}>
              📷 Add photo
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: "none" }} />
            </label>
          )}
        </div>

        {/* Title */}
        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>Title (optional)</label>
          <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="Brief title..." />
        </div>

        {/* Description */}
        <div style={{ marginBottom: "24px" }}>
          <label style={labelStyle}>Description</label>
          <textarea
            style={{ ...inputStyle, minHeight: "140px", resize: "vertical" }}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Describe the issue..."
          />
        </div>

        {error && (
          <div style={{ backgroundColor: "#fdecea", border: `1px solid ${colors.red}`, borderRadius: "6px", padding: "10px 12px", color: colors.red, fontSize: "13px", marginBottom: "14px" }}>
            {error}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1, backgroundColor: saving ? colors.slate : colors.teal,
              color: "white", border: "none", padding: "13px", borderRadius: "8px",
              fontSize: "14px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
          {!report.is_resolved && (
            <button
              onClick={handleResolve}
              disabled={resolving}
              style={{
                flex: 1, backgroundColor: "transparent",
                color: resolving ? colors.slate : colors.green,
                border: `2px solid ${resolving ? colors.slate : colors.green}`,
                padding: "13px", borderRadius: "8px",
                fontSize: "14px", fontWeight: "700", cursor: resolving ? "not-allowed" : "pointer",
              }}
            >
              {resolving ? "..." : "Mark Resolved"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ManagementReports() {
  const isMobile = useIsMobile();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(null);
  const [showOpen, setShowOpen] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editReport, setEditReport] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/field-reports/`);
      setReports(res.data);
    } catch {}
    finally { setLoading(false); }
  };

  const handleResolve = async (report) => {
    setResolvingId(report.id);
    try {
      await axios.patch(`${API}/field-reports/${report.id}/`, { is_resolved: true });
      setReports(prev => prev.map(r => r.id === report.id ? { ...r, is_resolved: true } : r));
    } catch {}
    finally { setResolvingId(null); }
  };

  const filtered = reports.filter(r => {
    if (filterType && r.report_type !== filterType) return false;
    if (showOpen && r.is_resolved) return false;
    return true;
  });

  const openCount = reports.filter(r => !r.is_resolved).length;
  const pad = isMobile ? "12px" : "24px";

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", padding: pad }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", marginBottom: "20px" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: colors.darkGrey }}>Reports</h1>
          {openCount > 0 && <div style={{ fontSize: "12px", color: colors.red, fontWeight: "600", marginTop: "2px" }}>{openCount} open</div>}
        </div>
        <button
          onClick={() => setShowNewForm(true)}
          style={{ backgroundColor: colors.darkGrey, color: "white", border: "none", padding: "10px 18px", borderRadius: "8px", fontSize: "13px", fontWeight: "700", cursor: "pointer" }}
        >
          + New Report
        </button>
      </div>

      {/* Filters */}
      <div style={{ backgroundColor: colors.white, border: `1px solid ${colors.border}`, borderRadius: "10px", padding: "12px 16px", marginBottom: "16px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
          <button
            onClick={() => setShowOpen(v => !v)}
            style={{
              padding: "5px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
              border: `1px solid ${showOpen ? colors.teal : colors.border}`,
              backgroundColor: showOpen ? colors.teal : "transparent",
              color: showOpen ? "white" : colors.slate,
            }}
          >
            Open only
          </button>
          <div style={{ width: "1px", height: "20px", backgroundColor: colors.border, margin: "0 4px" }} />
          <button
            onClick={() => setFilterType(null)}
            style={{
              padding: "5px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
              border: `1px solid ${!filterType ? colors.darkGrey : colors.border}`,
              backgroundColor: !filterType ? colors.darkGrey : "transparent",
              color: !filterType ? "white" : colors.slate,
            }}
          >
            All Types
          </button>
          {ALL_TYPES.map(t => (
            <button
              key={t.id}
              onClick={() => setFilterType(filterType === t.id ? null : t.id)}
              style={{
                padding: "5px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
                border: `1px solid ${filterType === t.id ? t.color : colors.border}`,
                backgroundColor: filterType === t.id ? `${t.color}15` : "transparent",
                color: filterType === t.id ? t.color : colors.slate,
                display: "flex", alignItems: "center", gap: "4px",
              }}
            >
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Report grid */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px", color: colors.slate }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px", color: colors.slate, backgroundColor: colors.white, borderRadius: "10px", border: `1px solid ${colors.border}` }}>
          {showOpen ? "No open reports" : "No reports found"}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "12px" }}>
          {filtered.map(report => {
            const t = typeInfo(report.report_type);
            const isCritical = CRITICAL_TYPES.has(report.report_type);
            return (
              <div
                key={report.id}
                onClick={() => setEditReport(report)}
                style={{
                  backgroundColor: colors.white,
                  border: `1px solid ${colors.border}`,
                  borderLeft: `4px solid ${isCritical && !report.is_resolved ? t.color : (report.is_resolved ? "#94a3b8" : t.color)}`,
                  borderRadius: "10px",
                  padding: "16px",
                  opacity: report.is_resolved ? 0.7 : 1,
                  cursor: "pointer",
                  transition: "box-shadow 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.1)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
              >
                {/* Type + status row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "16px" }}>{t.icon}</span>
                    <span style={{ fontWeight: "700", fontSize: "13px", color: t.color }}>{t.label}</span>
                  </div>
                  <span style={{
                    fontSize: "10px",
                    backgroundColor: report.is_resolved ? "#e8f8f1" : (isCritical ? "#fdecea" : "#fef9e7"),
                    color: report.is_resolved ? colors.green : (isCritical ? colors.red : colors.amber),
                    padding: "3px 8px", borderRadius: "12px", fontWeight: "700",
                  }}>
                    {report.is_resolved ? "Resolved" : "Open"}
                  </span>
                </div>

                {/* Site */}
                <div style={{ fontWeight: "600", fontSize: "13px", color: colors.darkGrey }}>{report.site_name}</div>
                <div style={{ fontSize: "11px", color: colors.slate, marginBottom: "8px" }}>{report.site_address}</div>

                {/* Title + description + photo */}
                {report.title && (
                  <div style={{ fontSize: "12px", fontStyle: "italic", color: colors.slate, marginBottom: "4px" }}>{report.title}</div>
                )}
                <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                  <div style={{ flex: 1, fontSize: "13px", color: "#374151", lineHeight: "1.4" }}>{report.description}</div>
                  {report.photo_url && (
                    <a href={report.photo_url} target="_blank" rel="noopener noreferrer">
                      <img src={report.photo_url} alt="Report" style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "6px", border: `1px solid ${colors.border}`, flexShrink: 0 }} />
                    </a>
                  )}
                </div>

                {/* Meta row */}
                <div style={{ marginTop: "10px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                  <div>
                    <div style={{ fontSize: "11px", color: colors.slate }}>{report.submitted_by_name} · {fmtDate(report.submitted_at)}</div>
                    {report.is_resolved && report.resolved_at && (
                      <div style={{ fontSize: "10px", color: colors.green }}>Resolved {fmtDate(report.resolved_at)}</div>
                    )}
                  </div>
                  {!report.is_resolved && (
                    <button
                      onClick={e => { e.stopPropagation(); handleResolve(report); }}
                      disabled={resolvingId === report.id}
                      style={{
                        padding: "6px 14px", borderRadius: "6px", border: `1px solid ${colors.green}`,
                        backgroundColor: "transparent", color: colors.green,
                        fontSize: "12px", fontWeight: "700", cursor: "pointer",
                      }}
                    >
                      {resolvingId === report.id ? "..." : "Mark Resolved"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New report slide-in */}
      {showNewForm && (
        <ReportForm
          onClose={() => setShowNewForm(false)}
          onSuccess={() => { setShowNewForm(false); fetchReports(); }}
        />
      )}

      {/* Edit report slide-in */}
      {editReport && (
        <EditReportPanel
          report={editReport}
          onClose={() => setEditReport(null)}
          onSaved={(updated) => {
            setReports(prev => prev.map(r => r.id === updated.id ? updated : r));
            setEditReport(null);
          }}
          onResolved={(id) => {
            setReports(prev => prev.map(r => r.id === id ? { ...r, is_resolved: true } : r));
            setEditReport(null);
          }}
        />
      )}
    </div>
  );
}
